import { SERVICE_AREAS, PHONE_DISPLAY, PHONE_TEL, TID } from "@/config/site";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";

export default function ServiceAreas() {
  const leftRef = useReveal();
  const rightRef = useReveal(0.05);
  return (
    <section id="areas" className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/5 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{ background: "radial-gradient(ellipse at 90% 60%, rgba(59,130,246,0.10) 0%, rgba(0,0,0,0) 55%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
        <div ref={leftRef} className="lux-reveal lg:col-span-5">
          <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
            Service Areas
          </div>
          <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Serving <span className="text-yellow-400">7 Eastside cities.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-base sm:text-lg font-light leading-relaxed">
            Click your city for local content — neighborhoods, highways, landmarks, and answers to the questions people ask us most.
          </p>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="areas-call-btn"
            className="mt-8 inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-3.5 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> {PHONE_DISPLAY}
          </a>
        </div>

        <div ref={rightRef} className="lux-reveal lux-reveal-delay-2 lg:col-span-7">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {SERVICE_AREAS.map((c, i) => (
              <Link
                key={c.slug}
                to={c.path || `/tow-truck-${c.slug}`}
                data-testid={TID.areaChip(c.name)}
                className={`group lux-card lux-glass rounded-xl px-4 py-4 flex items-center justify-between gap-3 lux-reveal lux-reveal-delay-${(i % 4) + 1}`}
              >
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-yellow-400 shrink-0" strokeWidth={2} />
                  <div>
                    <div className="font-display font-black uppercase text-white text-lg leading-none tracking-tight">
                      {c.name}
                    </div>
                    <div className="font-display uppercase tracking-[0.22em] text-[10px] text-zinc-500 mt-1">
                      Tow Truck · {c.zip}
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-yellow-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" strokeWidth={2.5} />
              </Link>
            ))}
          </div>

          <div className="mt-6 lux-glass rounded-xl p-5 text-zinc-400 text-sm font-light leading-relaxed">
            <span className="font-display uppercase tracking-[0.22em] text-yellow-400 text-xs">
              Also serving:
            </span>{" "}
            Sammamish, Medina, Clyde Hill, Mercer Island, Juanita, Totem Lake, Rose Hill, Bridle Trails, Crossroads, Eastgate, Factoria, Somerset, Canyon Park, North Creek — and highways I-405, I-90, SR-520 and SR-522.
          </div>

          <Link
            to="/kirkland-towing"
            data-testid="areas-kirkland-landing-link"
            className="mt-4 inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 font-display uppercase tracking-[0.22em] text-xs group"
          >
            <MapPin className="h-3.5 w-3.5" strokeWidth={2.5} />
            24/7 Towing Service in Kirkland, WA
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
